import { useGetOrderOverviews } from 'ClientPortal/src/api/Queries/orders'
import { type ThreadWithMetadata, useGetSortedAndFilteredThreadsWithMetadata } from 'ClientPortal/src/api/Queries/threads'
import { useGetUsers, usePinThread } from 'ClientPortal/src/api/Queries/users'
import { useCreateNewThread } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/useCreateThread'
import { isOrderOverview } from 'ClientPortal/src/api/Queries/orders'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import invariant from 'tiny-invariant'

type UseThreadOverviewPageProps = {
    orderFilterValue: UUID | undefined
}

export function useThreadOverviewPage({ orderFilterValue }: UseThreadOverviewPageProps) {
    const { orderOverviews, isOrderOverviewsPending } = useGetOrderOverviews()
    const { filteredAndSortedThreads, isThreadsPending, pinnedThreads } = useGetSortedAndFilteredThreadsWithMetadata({
        orderId: orderFilterValue,
    })

    const actualOrderOverviews =
        orderOverviews?.orders
            .filter(isOrderOverview)
            .filter((order) => order.status !== 'temp_hide_in_portal')
            .sort((a, b) => {
                invariant(b.order_date && a.order_date, 'order overview / order must have an order date')
                return b.order_date.localeCompare(a.order_date)
            }) || []

    const threadsWithTaskAndOrder = filteredAndSortedThreads.map((thread) => {
        Object.assign(thread, {
            order: actualOrderOverviews.find((order) => order.uuid === thread.order_uuid),
        })
        return thread
    })

    const threads = threadsWithTaskAndOrder.filter((thread) => !isUnusedSystemMessageThread(thread))

    const { users } = useGetUsers({ orderId: orderFilterValue })

    const { pinThread, unpinThread, isPatchingUser: isPatchingPin } = usePinThread({ pinnedThreads })

    return {
        users,
        threads,
        isOrderOverviewsPending,
        isThreadsPending,
        actualOrderOverviews,
        ...useCreateNewThread(),
        pinThread,
        unpinThread,
        isPatchingPin,
    }
}

type Thread = Pick<ThreadWithMetadata, 'messages' | 'associatedTask'>

function isUnusedSystemMessageThread(thread: Thread) {
    return thread.messages.length === 1 && thread.messages[0].message_type === 'system_message'
}
